import { useImmer } from "use-immer";
import ApiConnector from "../connector/ApiConnector";
import { useEffect, useRef, useState } from "react";
import UtilService from "../service/UtilService";
import S3Connector from "../connector/S3Connector";
import { faFloppyDisk } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTableModel } from "../service/TableModelUtilService";
import { normalizeRowsForSignature } from "../service/NormalizeForSignatureUtilService";

const Component = (props) => {
  const [s3Files, updateS3Files] = useImmer([]);

  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [fileType, setFileType] = useState("transactions");
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [status, setStatus] = useState("Nothing to do");

  const { canSave, setRows: setModelRows, setBaselineFromRows } = useTableModel({
    initialRows: [],
    columns: [],
    keyField: "_key",
    initialSortBy: null,
    initialSortDir: "asc",
    normalizeForSignature: normalizeRowsForSignature,
  });

  useEffect(() => {
    loadListOfS3Files();
    const initialRow = { fileType, fileName: null, fileSize: null };
    setModelRows([initialRow]);
    setBaselineFromRows([initialRow]);
  }, []);

  useEffect(() => {
    const row = { fileType, fileName: file?.name ?? null, fileSize: file?.size ?? null };
    setModelRows([row]);
  }, [fileType, file]); // <-- odstraněn setModelRows z deps

  const loadListOfS3Files = () => {
    S3Connector.getListOfS3Files().then((t) => {
      t = t.sort((a, b) => b.lastModified.localeCompare(a.lastModified));
      updateS3Files(t);
    });
  };

  const processFile = async () => {
    if (isUploadInProgress) {
      window.alert("There is already upload in progress.");
      return;
    }
    if (!file) {
      window.alert("Please choose a file to upload.");
      return;
    }

    const result = window.confirm(
      "Are you sure you want to store a file type " +
      fileType +
      " " +
      file.name +
      " " +
      "to Pablo? This operation may take up to 20 minutes (depending on size) and once started it cannot be taken back. " +
      "Do not start any other operation during this process and wait until it's finished."
    );
    if (!result) return;

    try {
      setIsUploadInProgress(true);
      const fileName = file.name.replace(/\s/g, "");
      setStatus("Obtaining url for file upload...");
      const resp = await S3Connector.getS3FileUploadUrl(fileName);
      const uploadUrl = resp.upload_url;
      setStatus("Uploading file...");
      await S3Connector.uploadFileToS3(uploadUrl, file, fileName);
      setStatus("Processing file. Notification will be sent to Slack when finished.");
      switch (fileType) {
        case "transactions":
          await ApiConnector.storeTransactionsFromXlsx(fileName);
          break;
        case "products":
          await ApiConnector.storeProductsFromXlsx(fileName);
          break;
        case "frauds":
          await ApiConnector.storeFraudsFromXlsx(fileName);
          break;
        default:
          console.error("Unknown type");
      }
    } catch (e) {
      setStatus("Failed: " + e.message);
      console.log(e);
    }
    setIsUploadInProgress(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    const baseline = { fileType, fileName: null, fileSize: null };
    setModelRows([baseline]);
    setBaselineFromRows([baseline]);
    loadListOfS3Files();
  };

  const canClickSave = canSave && !!file && !isUploadInProgress;

  return (
    s3Files && (
      <>
        <header className="subheader header-with-save">
          <h1 className="header-title">Import</h1>
          <span className="header-save">
            <FontAwesomeIcon
              icon={faFloppyDisk}
              size="2xl"
              className="icon-clickable"
              onClick={canClickSave ? processFile : undefined}
              title={canClickSave ? "Upload & process file" : "No changes to save"}
              style={{
                opacity: canClickSave ? 1 : 0.4,
                cursor: canClickSave ? "pointer" : "not-allowed",
                pointerEvents: canClickSave ? "auto" : "none",
              }}
              aria-disabled={!canClickSave}
            />
          </span>
        </header>

        <main>
          <div className="row">
            <div className="col">
              <h2>Import data</h2>
            </div>
            <div className="col" />
          </div>

          <table className="config-table table-with-button">
            <thead>
            <tr>
              <th>Import type</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>
                <select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                >
                  <option value="transactions">Transactions xlsx</option>
                  <option value="products">Products xlsx</option>
                  <option value="frauds">Frauds xlsx</option>
                </select>
              </td>
              <td>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
              </td>
            </tr>
            </tbody>
          </table>

          <table>
            <thead>
            <tr>
              <th>Import status</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>{status}</td>
            </tr>
            </tbody>
          </table>

          <h3>Uploaded files</h3>

          <table className="config-table table-with-button">
            <thead>
            <tr>
              <th>File name</th>
              <th>Last modified</th>
              <th>Size [Mb]</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            {s3Files.map((entry, idx) => (
              <tr key={idx}>
                <td>{entry.key}</td>
                <td>{UtilService.isoDateToReadable(entry.lastModified)}</td>
                <td>{Math.round((entry.size / 1024 / 1024) * 100) / 100}</td>
                <td></td>
              </tr>
            ))}
            </tbody>
          </table>
        </main>
      </>
    )
  );
};

export default Component;
